import React, { useState, Suspense, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Container, Box, Typography, Tabs, Tab } from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import {
  ProjectModule,
  ProjectDetailTabs,
  ProjectStatus,
  CreateProjectRequest,
} from "../types";
import NavigationHeader from "../../../components/NavigationHeader";
import { useToast } from "../../../components/Toast";
import { projectModuleApi, superProjectApi } from "../services/apiService";
import { ProjectTabEnum, PROJECT_TERMINOLOGY } from "../enums";
import { ProjectDetailViewSkeleton } from "../components/skeletons";
import ConfirmationDialog from "../components/ConfirmationDialog";
import ProjectBasicDetailView from "../components/ProjectBasicDetailView";
import { UserScope } from "../../../types/operationType";
import { usePlanStatus } from "../../../hooks/usePlanStatus";
import ModuleActionModal from "../components/ModuleActionModal";

// Lazy load tab components
const ContractTab = React.lazy(() => import("../components/tabs/ContractTab"));
const SummaryTab = React.lazy(() => import("../components/tabs/SummaryTab"));
const DivisionTab = React.lazy(() => import("../components/tabs/DivisionTab"));
const DocumentsTab = React.lazy(
  () => import("../components/tabs/DocumentsTab")
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: ProjectDetailTabs;
  value: ProjectDetailTabs | null;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index} style={{ padding: "16px 0" }}>
      {value === index && children}
    </div>
  );
};

const SuperProjectView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { planStatus } = usePlanStatus();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [superProject, setSuperProject] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get user's accessible tabs
  const getUserTabs = (): string[] => {
    // If user has GLOBAL scope, return all available tabs
    if (planStatus.user?.userRole?.scope === UserScope.GLOBAL) {
      return Object.values(ProjectDetailTabs);
    }

    if (!superProject?.userTabs || superProject.userTabs.length === 0) {
      return [];
    }
    return superProject.userTabs;
  };

  // Get initial tab based on user access and URL params
  const getInitialTab = (): ProjectDetailTabs | null => {
    const tabFromUrl = searchParams.get("tab");
    const userTabs = getUserTabs();

    // If user has no accessible tabs, return null
    if (userTabs.length === 0) {
      return null;
    }

    // If there's a valid tab in URL and user has access to it
    if (
      tabFromUrl &&
      Object.values(ProjectDetailTabs).includes(
        tabFromUrl as ProjectDetailTabs
      ) &&
      userTabs.includes(tabFromUrl)
    ) {
      return tabFromUrl as ProjectDetailTabs;
    }

    // Default to first accessible tab
    return userTabs[0] as ProjectDetailTabs;
  };

  const [activeTab, setActiveTab] = useState<ProjectDetailTabs | null>(null);

  const userTabs = getUserTabs();

  const fetchSuperProject = async () => {
    if (!id) {
      showToast(`${PROJECT_TERMINOLOGY.SINGULAR} ID not found`, "error");
      navigate(`/super`);
      return;
    }

    setIsLoading(true);
    try {
      const queryParams = "includeDivisions=false&includeContract=false";
      const data = await superProjectApi.getSuperProject(id, queryParams);
      setSuperProject(data);
    } catch (error) {
      showToast(
        `Failed to fetch ${PROJECT_TERMINOLOGY.LOWER_SINGULAR} details`,
        "error"
      );
      navigate(`/super`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSuperProject();
  }, []);

  // Update activeTab when superProject loads or URL parameters change
  useEffect(() => {
    if (!superProject) return;

    const initialTab = getInitialTab();
    if (initialTab) {
      setActiveTab(initialTab);
      setSearchParams((prev) => {
        prev.set("isSuperProject", "true");
        prev.set("tab", initialTab);
        return prev;
      });
    }
  }, [superProject, planStatus.user?.userRole?.scope]);

  const handleTerminateModule = async (superProject: ProjectModule) => {
    if (!superProject) return;
    try {
      await projectModuleApi.updateModuleStatus(
        superProject.id,
        ProjectStatus.TERMINATED
      );
      showToast(
        `${PROJECT_TERMINOLOGY.SINGULAR} terminated successfully`,
        "success"
      );
    } catch (error) {
      showToast(
        `Failed to terminate ${PROJECT_TERMINOLOGY.LOWER_SINGULAR}`,
        "error"
      );
    } finally {
      setTerminateDialogOpen(false);
    }
  };

  const handleUpdateModule = async (data: CreateProjectRequest) => {
    if (!superProject) return;
    setIsSubmitting(true);
    try {
      await superProjectApi.updateSuperProject(superProject.id, data);
      showToast(
        `${PROJECT_TERMINOLOGY.SINGULAR} updated successfully`,
        "success"
      );
      fetchSuperProject();
      setActionModalOpen(false);
    } catch (error) {
      showToast(
        `Failed to update ${PROJECT_TERMINOLOGY.LOWER_SINGULAR}`,
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTabChange = (
    _: React.SyntheticEvent,
    newValue: ProjectDetailTabs
  ) => {
    setActiveTab(newValue);
    // Update URL parameters
    setSearchParams((prev) => {
      prev.set("isSuperProject", "true");
      prev.set("tab", newValue);
      return prev;
    });
  };

  const handleEdit = () => {
    // navigate(
    //   `/super/form/${superProject?.id}`
    // );
    setActionModalOpen(true);
  };

  if (isLoading) {
    return <ProjectDetailViewSkeleton />;
  }

  if (!superProject) {
    return (
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <NavigationHeader
          title={`${PROJECT_TERMINOLOGY.SINGULAR} Not Found`}
          backNavigationTo={`/super`}
          breadcrumbs={[
            {
              label: PROJECT_TERMINOLOGY.PLURAL,
              path: `/super`,
            },
            { label: `${PROJECT_TERMINOLOGY.SINGULAR} Details` },
          ]}
        />
        <Typography variant="h6" sx={{ textAlign: "center", mt: 4 }}>
          {PROJECT_TERMINOLOGY.SINGULAR} not found
        </Typography>
      </Container>
    );
  }

  // If user has no accessible tabs, show access denied
  if (userTabs.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <NavigationHeader
          title={`${PROJECT_TERMINOLOGY.SINGULAR} Details`}
          backNavigationTo={`/super`}
          breadcrumbs={[
            {
              label: PROJECT_TERMINOLOGY.PLURAL,
              path: `/super`,
            },
            { label: `${PROJECT_TERMINOLOGY.SINGULAR} Details` },
          ]}
        />
        <Typography variant="h6" sx={{ textAlign: "center", mt: 4 }}>
          You don't have access to this {PROJECT_TERMINOLOGY.LOWER_SINGULAR}
        </Typography>
      </Container>
    );
  }

  // If activeTab is not set yet, show loading
  if (!activeTab) {
    return <ProjectDetailViewSkeleton />;
  }

  const allTabConfig = [
    {
      key: ProjectDetailTabs.SUMMARY,
      label: ProjectTabEnum.SUMMARY.label,
      icon: React.cloneElement(ProjectTabEnum.SUMMARY.icon, {
        sx: { fontSize: "1rem" },
      }),
    },
    {
      key: ProjectDetailTabs.DIVISIONS,
      label: ProjectTabEnum.DIVISIONS.label,
      icon: React.cloneElement(ProjectTabEnum.DIVISIONS.icon, {
        sx: { fontSize: "1rem" },
      }),
    },
    {
      key: ProjectDetailTabs.CONTRACT,
      label: ProjectTabEnum.CONTRACT.label,
      icon: React.cloneElement(ProjectTabEnum.CONTRACT.icon, {
        sx: { fontSize: "1rem" },
      }),
    },
    {
      key: ProjectDetailTabs.DOCUMENTS,
      label: ProjectTabEnum.DOCUMENTS.label,
      icon: React.cloneElement(ProjectTabEnum.DOCUMENTS.icon, {
        sx: { fontSize: "1rem" },
      }),
    },
  ];

  // Filter tabs based on user's access
  const tabConfig = allTabConfig.filter((tab) => userTabs.includes(tab.key));

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <NavigationHeader
        title={`${PROJECT_TERMINOLOGY.SINGULAR} Details`}
        backNavigationTo={`/super`}
        breadcrumbs={[
          {
            label: PROJECT_TERMINOLOGY.PLURAL,
            path: `/super`,
          },
          { label: `${PROJECT_TERMINOLOGY.SINGULAR} Details` },
        ]}
        buttons={
          planStatus.user?.userRole?.scope === UserScope.GLOBAL
            ? [
                {
                  label: `Edit ${PROJECT_TERMINOLOGY.SINGULAR}`,
                  onClick: handleEdit,
                  icon: <EditIcon />,
                  variant: "outlined",
                },
                // ...(superProject.status !== ProjectStatus.TERMINATED
                //   ? [
                //       {
                //         label: "Terminate",
                //         onClick: () => setTerminateDialogOpen(true),
                //         icon: <CancelIcon sx={{ fontSize: "0.6rem" }} />,
                //         variant: "outlined" as const,
                //         color: "error" as const,
                //       },
                //     ]
                //   : []),
              ]
            : []
        }
      />

      {/* Basic Project Information */}
      <ProjectBasicDetailView projectDetails={superProject as ProjectModule} />

      {/* Tabs Section */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab || false}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTab-root": {
              fontSize: "0.75rem",
              minHeight: "48px",
              textTransform: "none",
            },
          }}
        >
          {tabConfig.map((tab) => (
            <Tab
              key={tab.key}
              value={tab.key}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              onClick={() => {
                setActiveTab(tab.key);
                setSearchParams((prev) => {
                  prev.set("isSuperProject", "true");
                  prev.set("tab", tab.key);
                  return prev;
                });
              }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box>
        <Suspense
        // fallback={
        //   <Loading message="Loading tab content..." fullScreen={false} />
        // }
        >
          {userTabs.includes(ProjectDetailTabs.SUMMARY) && (
            <TabPanel value={activeTab} index={ProjectDetailTabs.SUMMARY}>
              <SummaryTab
                projectId={superProject.id}
                setActiveTab={setActiveTab}
                superProjectId={superProject.id}
                shouldRedirect={false}
              />
            </TabPanel>
          )}
          {userTabs.includes(ProjectDetailTabs.DIVISIONS) && (
            <TabPanel value={activeTab} index={ProjectDetailTabs.DIVISIONS}>
              <DivisionTab superProjectId={superProject.id} />
            </TabPanel>
          )}
          {userTabs.includes(ProjectDetailTabs.CONTRACT) && (
            <TabPanel value={activeTab} index={ProjectDetailTabs.CONTRACT}>
              <ContractTab projectId={superProject?.id || ""} />
            </TabPanel>
          )}
          {userTabs.includes(ProjectDetailTabs.DOCUMENTS) && (
            <TabPanel value={activeTab} index={ProjectDetailTabs.DOCUMENTS}>
              <DocumentsTab projectId={superProject?.id || ""} />
            </TabPanel>
          )}
        </Suspense>
      </Box>
      {/* Modals */}
      <ModuleActionModal
        open={actionModalOpen}
        onClose={() => setActionModalOpen(false)}
        module={superProject as ProjectModule}
        onSubmit={handleUpdateModule}
        isSubmitting={isSubmitting}
      />

      {/* Terminate Module Dialog */}
      <ConfirmationDialog
        deleteModalOpen={terminateDialogOpen}
        handleNo={() => setTerminateDialogOpen(false)}
        handleYes={handleTerminateModule}
        itemToDelete={superProject}
        title={`Terminate ${PROJECT_TERMINOLOGY.SINGULAR}`}
        message={`Are you sure you want to terminate this ${PROJECT_TERMINOLOGY.LOWER_SINGULAR}? This action cannot be undone.`}
        action="Terminate"
      />
    </Container>
  );
};

export default SuperProjectView;
